import { Component, OnInit } from '@angular/core';
import { FcGenericItemModalBaseComponent, FcListValidationService } from '../../generic';
// eslint-disable-next-line no-restricted-imports
import { FormControl, FormGroup } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import { FcFormLayout, FcFormLayoutDto, FormGroupValidationRules, Guid, GuidStrValue } from 'webcommon/shared';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-form-layout-list-modal',
  templateUrl: './fc-form-layout-list-modal.component.html'
})
export class FcFormLayoutListModalComponent extends FcGenericItemModalBaseComponent implements OnInit {
  public override baseId = 'fc-form-layout';

  // Type-safe property name constants - force strings to match dto properties
  public readonly validationFields = {
    ExternalID: 'ExternalID' satisfies keyof FcFormLayoutDto,
    Name: 'Name' satisfies keyof FcFormLayoutDto,
    Notes: 'Notes' satisfies keyof FcFormLayoutDto,
    UIControlUid: 'UIControlUid' satisfies keyof FcFormLayoutDto,
    UIConfigurationUid: 'UIConfigurationUid' satisfies keyof FcFormLayoutDto,
    TitleDefinition: 'TitleDefinition' satisfies keyof FcFormLayoutDto,
    ProcedureUid: 'ProcedureUid' satisfies keyof FcFormLayoutDto,
  } as const;

  public idControl = new FormControl<string | undefined>('');
  public externalIdControl = new FormControl<string | undefined>('');
  public nameControl = new FormControl<string | undefined>('');
  public notesControl = new FormControl<string | undefined>('');
  public inactiveControl = new FormControl<boolean | undefined>(false);
  public userDefinedControl = new FormControl<boolean | undefined>(true);
  public uiControlControl = new FormControl<GuidStrValue[] | undefined>([]);
  public uiConfigurationControl = new FormControl<GuidStrValue[] | undefined>([]);
  public titleDefinitionControl = new FormControl<string | undefined>('');
  public requireDataTagControl = new FormControl<boolean>(false);
  public allowMultiControl = new FormControl<boolean>(false);
  public procedureControl = new FormControl<GuidStrValue[]>([]);

  public uiControlPkList?: GuidStrValue[] = [];
  public uiConfigurationPkList?: GuidStrValue[] = [];
  public procedurePkList?: GuidStrValue[] = [];

  public form: FormGroup = new FormGroup({
    id: this.idControl,
    externalId: this.externalIdControl,
    name: this.nameControl,
    notes: this.notesControl,
    inactive: this.inactiveControl,
    userDefined: this.userDefinedControl,
    titleDefinition: this.titleDefinitionControl,
    requireDataTag: this.requireDataTagControl,
    allowMulti: this.allowMultiControl,
    uiControl: this.uiControlControl,
    uiConfiguration: this.uiConfigurationControl,
    procedure: this.procedureControl,
  });

  public listItem: FcFormLayoutDto = {
    Id: '',
    ExternalID: undefined,
    Inactive: false,
    Name: undefined,
    Notes: undefined,
    UserDefined: true,
    UIControlUid: Guid._coerceToGuidStr(''),
    RequireDataTag: false,
    AllowMulti: false,
    TitleDefinition: '',
    UIConfigurationUid: Guid._coerceToGuidStr(''),
    ProcedureUid: undefined,
  };

  public listItemDefaultValidation: FormGroupValidationRules = {};

  override ngOnInit(): void {
    super.ngOnInit();

    this.loadListItem(this.listItemDefaultValidation);
  }

  public getGeneralFormErrors(): number {
    let count = 0;
    Object.keys(this.form.controls).forEach(x => {
      if (this.form.controls[x].invalid && !x.includes('Security')) {
        count++;
      }
    });
    return count;
  }

  override setForm(): void {
    this.requireDataTagControl.setValue(this.listItem?.RequireDataTag);
    this.allowMultiControl.setValue(this.listItem?.AllowMulti);

    this.idControl.setValue(this.listItem?.Id);
    // External ID Control
    this.externalIdControl.setValidators(this.fcListValidation.getValidators(this.validationFields.ExternalID));
    this.externalIdControl.setValue(this.listItem?.ExternalID);

    // Name Control
    const isNameControlDisabled = !this.listItem.UserDefined;
    if (isNameControlDisabled) {
      this.nameControl.disable();
    }
    this.nameControl.setValidators(this.fcListValidation.getValidators(this.validationFields.Name));
    this.nameControl.setValue(this.listItem?.Name);

    // Notes Control
    this.notesControl.setValidators(this.fcListValidation.getValidators(this.validationFields.Notes));
    this.notesControl.setValue(this.listItem?.Notes);

    //UIControlUid
    this.uiControlPkList = !this.listItem?.UIControlUid ? [] : [this.listItem.UIControlUid];
    this.uiControlControl.setValidators(this.fcListValidation.getValidators(this.validationFields.UIControlUid));
    this.uiControlControl.setValue(this.uiControlPkList);

    //UIConfigurationUid
    this.uiConfigurationPkList = !this.listItem?.UIConfigurationUid ? [] : [this.listItem.UIConfigurationUid];
    this.uiConfigurationControl.setValidators(this.fcListValidation.getValidators(this.validationFields.UIConfigurationUid));
    this.uiConfigurationControl.setValue(this.uiConfigurationPkList);

    // Title Definition
    this.titleDefinitionControl.setValidators(this.fcListValidation.getValidators(this.validationFields.TitleDefinition));
    this.titleDefinitionControl.setValue(this.listItem?.TitleDefinition);

    // ProcedureUid
    this.procedurePkList = !this.listItem?.ProcedureUid ? [] : [this.listItem.ProcedureUid];
    this.procedureControl.setValidators(this.fcListValidation.getValidators(this.validationFields.ProcedureUid));
    this.procedureControl.setValue(this.procedurePkList);

    // Inactive Control
    this.inactiveControl.setValue(this.listItem.Inactive);

    // User Defined Control
    this.userDefinedControl.disable();
    this.userDefinedControl.setValue(this.listItem.UserDefined);
  }

  public override save(): void {
    this.isSubmitted = true;
    const values = this.form.getRawValue();
    const itemDto: FcFormLayout = {
      ...(values.id && { Id: values.id }),
      ExternalID: values.externalId ?? '',
      Name: values.name,
      Notes: values.notes,
      Inactive: values.inactive,
      UserDefined: values.userDefined,
      UIControlUid: !values.uiControl?.length ? '' : values.uiControl[0],
      UIConfigurationUid: !values.uiConfiguration?.length ? '' : values.uiConfiguration[0],
      ProcedureUid: values.procedure,
      RequireDataTag: values.requireDataTag,
      TitleDefinition: values.titleDefinition,
      AllowMulti: values.allowMulti,
    };

    super.save<FcFormLayout>(itemDto);
  }
}
